// ── add/remove-edit result re-hydration (OG GenerateMailMerge re-merge parity) ──────
// When a saved QC batch is edited (a sample/QC added or removed), OG re-opens the SDMS
// sheet by rebuilding it FRESH from the template (GenerateMailMerge) but re-hydrating each
// RETAINED row's entered results from the persisted SpreadSheetEntry rows, matched by
// SystemID+Parameter (SpreadsheetEntryController.GenerateMailMerge / BindQCBatchEditSequence
// ToDataTable). Added rows come up blank; removed rows are absent; there is NO results/status
// gate — add/remove is permitted after results are entered.
//
// AI mirrors that: the edit handoff builds the sheet from the STAGED sequence
// (sdmsMergeData → blank cells) and overlays the entered/computed values from the batch's
// persisted rows (sdmsOpenMergeData) onto the matching staged records.

/** Entered/computed value columns EVERY template can carry (the fixed result/metric/status
 *  slots). Normalized (uppercase, brackets already stripped).
 *
 *  This is deliberately NOT the whole story: the columns a given template actually ENTERS
 *  (Titrant Volume → VolumeUsed on Alkalinity 8085, VT/VT1/Mass on others…) are derived
 *  server-side per template (`entryFields` on the reopen feed — Write=1 minus merge-supplied
 *  minus rounded-on-save) and passed to `overlaySdmsEnteredResults` as `extraKeys`. Keeping
 *  them in a hand list here is what blanked every Titrant Volume (and so every Result
 *  formula) after an add/remove edit on 8085 (user report 2026-09-22). */
export const SDMS_ENTERED_VALUE_KEYS = new Set<string>([
  'READING', 'READING1', 'READING2', 'NUMERICRESULT', 'RESULT', '%RECOVERY', 'RPD',
  'QUALIFIER', 'QCSTATUS', 'CONCENTRATION', 'RESPONSE', 'DUPSOURCERESULT',
  'SPIKESOURCERESULT', 'REMARK',
  ...Array.from({ length: 36 }, (_, i) => `USERDEFINED${i + 1}`),
]);

/** Normalize a record key for comparison: uppercase + strip ONE pair of surrounding brackets
 *  (matches sdmsMailMerge.normKey, so an overlaid value lands where the merge lookup finds it). */
export const sdmsNormKey = (k: string): string => k.toUpperCase().replace(/^\[(.+)\]$/, '$1');

/** The SampleInfo natural key of a merge record: (SysSampleCode | RunNo | Parameter) — the same
 *  tuple a normal reopen (AI_Analysis_Sdms_OpenMergeData) relies on to place per-row results, so
 *  matching on it here is exactly as safe as reopen itself. */
export function sdmsRowKey(rec: Record<string, unknown>): string {
  const pick = (name: string): unknown => {
    for (const k of Object.keys(rec)) if (sdmsNormKey(k) === name) return rec[k];
    return undefined;
  };
  const s = (v: unknown): string => (v == null ? '' : String(v)).trim().toUpperCase();
  return `${s(pick('SYSSAMPLECODE'))}|${s(pick('RUNNO'))}|${s(pick('PARAMETER'))}`;
}

/** Carry entered/computed results from the batch's PERSISTED rows (`dbRecords`, from
 *  sdmsOpenMergeData) onto the STAGED final-set records (`seqRecords`, from sdmsMergeData),
 *  matched by the SampleInfo natural key. Only value columns are overlaid — the fixed
 *  `SDMS_ENTERED_VALUE_KEYS` plus `extraKeys` (the template's server-derived `entryFields`) —
 *  structure/order stays from the staged set. OG parity: retained rows keep their results,
 *  added rows have no match (stay blank), removed rows aren't in the staged set (already
 *  excluded). Mutates and returns `seqRecords`. */
export function overlaySdmsEnteredResults(
  seqRecords: Record<string, unknown>[] | undefined,
  dbRecords: Record<string, unknown>[] | undefined,
  extraKeys: readonly string[] | null = null,
): Record<string, unknown>[] {
  const seq = seqRecords ?? [];
  if (!seq.length || !dbRecords?.length) return seq;
  const keys = new Set<string>(SDMS_ENTERED_VALUE_KEYS);
  for (const k of extraKeys ?? []) { const nk = sdmsNormKey(k.trim()); if (nk) keys.add(nk); }
  const byKey = new Map<string, Record<string, unknown>>();
  for (const db of dbRecords) { const k = sdmsRowKey(db); if (!byKey.has(k)) byKey.set(k, db); }
  for (const rec of seq) {
    const db = byKey.get(sdmsRowKey(rec));
    if (!db) continue;
    for (const dk of Object.keys(db)) {
      const nk = sdmsNormKey(dk);
      if (!keys.has(nk)) continue;
      // Land the value under the staged record's OWN key for that column (avoids a duplicate
      // case-variant key that would make the merge lookup ambiguous).
      let target = dk;
      for (const rk of Object.keys(rec)) if (sdmsNormKey(rk) === nk) { target = rk; break; }
      rec[target] = db[dk];
    }
  }
  return seq;
}
